import { signal, computed } from "@preact/signals-core";
import { getCookie, setCookie } from "../utils/knocking";
import { themeSettings } from "./extended-offer-store";

/**
 * Promo-code capture state.
 *
 * A promo code is captured from the `?promo_code=` URL param or the promo-code
 * modal, validated against the merchant-defined list (theme settings), and
 * persisted in the `promo_code` cookie. The cart store reads that cookie on every
 * add and attaches it as the `_promo-code` line-item property — so the cookie is
 * the source of truth and cart-store needs no changes.
 */

const PROMO_COOKIE = "promo_code";
const COOKIE_DAYS = 7;

// 1. Raw state — the currently applied (valid) promo code, if any.
export const appliedPromoCode = signal<string | null>(null);

// 2. Computed state
/** Merchant-defined valid codes (upper-cased), sourced from theme settings. */
export const validPromoCodes = computed<string[]>(
  () => themeSettings.value?.promoCodes ?? []
);

/** Whether the promo-code modal feature is enabled in theme settings. */
export const isPromoCodeModalEnabled = computed<boolean>(
  () => themeSettings.value?.enablePromoCodeModal ?? false
);

export const hasValidPromoCode = computed<boolean>(
  () => appliedPromoCode.value !== null
);

// 3. Actions
/** Case-insensitive membership check against the merchant-defined codes. */
export const validatePromoCode = (code: string): boolean => {
  const normalized = code.trim().toUpperCase();
  if (!normalized) return false;
  return validPromoCodes.value.includes(normalized);
};

/** Extract a promo code from the current URL's `?promo_code=` param, if present. */
export const getUrlPromoCode = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("promo_code");
  return code ? code.trim().toUpperCase() : null;
};

/**
 * Validate and persist a promo code. On success the `promo_code` cookie is set
 * (7-day lifetime) and `appliedPromoCode` updated. Returns whether it was valid.
 */
export const applyPromoCode = (code: string): boolean => {
  const normalized = code.trim().toUpperCase();
  if (!validatePromoCode(normalized)) return false;
  setCookie(PROMO_COOKIE, normalized, COOKIE_DAYS);
  appliedPromoCode.value = normalized;
  return true;
};

/**
 * Bootstrap on page load: prefer a URL code, else fall back to an existing
 * cookie. If a valid code is found it is applied silently (no modal). Returns
 * true when a valid code was applied.
 */
export const initPromoCode = (): boolean => {
  const fromUrl = getUrlPromoCode();
  if (fromUrl) return applyPromoCode(fromUrl);

  const fromCookie = getCookie(PROMO_COOKIE);
  if (fromCookie && validatePromoCode(fromCookie)) {
    appliedPromoCode.value = fromCookie.trim().toUpperCase();
    return true;
  }
  return false;
};
